import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';

import { useDecodedToken } from 'src/hooks/useDecodedToken';
import { PATHS } from 'src/constants/config';
import { queryApi } from 'src/api/queryApi';
import { userApi } from 'src/api/userApi';

const fetcher = () => userApi.getCurrentUserInfo();

const EmailForm = ({ pitch, deadline, leadID, query, expert }) => {
  const [open, setOpen] = useState(false);
  const [emailBody, setEmailBody] = useState(pitch);
  const { data } = useSWR('currentUser', fetcher);
  const userDetails = data || null;
  const { decodedToken, isDemo } = useDecodedToken();
  const navigate = useNavigate();
  const formattedDeadline = format(parseISO(deadline), 'MMMM d h a').replace(':00', '');
  const subject = `PR Opportunity / Quote for Approval / ${formattedDeadline} Deadline`;

  const extractName = useCallback((fullName) => {
    const nameParts = fullName?.split(' ');
    if (nameParts?.length === 2) {
      return nameParts[0];
    } else if (nameParts?.length >= 3) {
      return `${nameParts[0]} ${nameParts[1]}`;
    }
    return fullName;
  }, []);

  const extractFirstTopic = useCallback((topics) => {
    if (typeof topics === 'string') {
      const topicsArray = topics.split(',').map((t) => t.trim());
      if (topicsArray.length > 0) {
        const firstTopic = topicsArray[0]
          .replace(/^["'[\s]+|["'\]\s]+$/g, '')
          .replace('#', '')
          .replace(/([A-Z])/g, ' $1')
          .trim();
        return firstTopic.charAt(0).toUpperCase() + firstTopic.slice(1);
      }
    }
    return 'the specified topic';
  }, []);

  const formatEmailBody = useCallback(() => {
    if (isDemo) return pitch;

    const formattedName = extractName(expert?.name);
    const topic = extractFirstTopic(query?.topics);
    return `Hi ${formattedName},

I hope you are well! I came across an opportunity with ${query.mediaOutlet} regarding ${topic}. Let me know if you have any edits you'd like to see made!

The deadline is ${formattedDeadline}.
----------------------------
${pitch}
----------------------------
Query: ${query.title}
Outlet: ${query.mediaOutlet}
Deadline: ${formattedDeadline}
----------------------------
Thank you so much,
${userDetails?.firstName}`;
  }, [expert, query, pitch, userDetails, extractName, extractFirstTopic, formattedDeadline, isDemo]);

  useEffect(() => {
    setEmailBody(formatEmailBody());
  }, [formatEmailBody, pitch, query, expert, userDetails]);

  const getEmails = () => {
    try {
      const pointsOfContact = JSON.parse(expert.pointOfContactJson || '[]');
      if (pointsOfContact.length > 0) {
        const emails = pointsOfContact.map((poc) => poc.email);
        return emails;
      }
    } catch (error) {
      return [expert?.email || 'email@example.com'];
    }
    return [expert?.email || 'email@example.com'];
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  const moveToAwaiting = async (skipNavigate = false) => {
    setOpen(false);

    try {
      const [leadsResponse, queriesResponse] = await Promise.all([
        queryApi.updateLead(leadID, emailBody),
        queryApi.updateQuery(query.id, 'awaiting', decodedToken?.userID, leadID),
      ]);

      console.log('Leads update response:', leadsResponse);
      console.log('Queries update response:', queriesResponse);

      if (!skipNavigate) {
        navigate(PATHS.PITCH_INBOX.value);
      }

      return { leadsResponse, queriesResponse };
    } catch (error) {
      console.error('Error in moveToAwaiting:', error);
      throw error; // Re-throw to ensure the error is propagated
    }
  };

  const sendEmail = () => {
    const emails = getEmails();
    const primaryEmail = emails.shift();
    const ccEmails = emails.join(',');

    const encodedBody = encodeURIComponent(emailBody).replace(/%20/g, '+').replace(/%0A/g, '%0D%0A');

    const url = `https://mail.google.com/mail/?fs=1&to=${primaryEmail}&cc=${ccEmails}&su=${subject}&body=${encodedBody}&tf=cm`;
    const height = window.screen.height * 0.8;
    const width = window.screen.width * 0.5;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const windowFeatures = `top=${top},left=${left},width=${width},height=${height},scrollbars=yes,resizable=yes`;

    window.open(url, 'EmailWindow', windowFeatures);
    handleOpenDialog();
  };

  const handleBodyChange = (event) => {
    setEmailBody(event.target.value);
  };

  return (
    <div>
      <TextField id="email-body" label="Email Body" multiline fullWidth rows={24} value={emailBody} onChange={handleBodyChange} variant="outlined" />
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="outlined" onClick={sendEmail} sx={{ backgroundColor: 'grey.200' }}>
          Send by email
        </Button>
      </Box>
      <Dialog open={open} onClose={handleCloseDialog} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Confirm Email Send</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Did you successfully send the email to the client?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={moveToAwaiting} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

EmailForm.propTypes = {
  pitch: PropTypes.string.isRequired,
  deadline: PropTypes.string.isRequired,
  leadID: PropTypes.number.isRequired,
  query: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    mediaOutlet: PropTypes.string.isRequired,
    topics: PropTypes.string.isRequired,
  }).isRequired,
  expert: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    pointOfContactJson: PropTypes.string,
  }).isRequired,
};

export default EmailForm;
